/**
 * `configuredOr(value, fallback)` — the legacy `(value) ? value : fallback` idiom, which
 * every ported widget needs and which is *not* `??`.
 *
 * The legacy app read every optional config value through a truthiness check, so an empty
 * string falls back to the default rather than emitting an empty CSS declaration. One
 * deployed config depends on it: `imageLabelWidget`'s `itemsLayout.font.family` is `""` in
 * all seven files, and `??` there would emit `font-family: ;` and drop the brand font.
 *
 * It exists as a function rather than as `||` at each call site because
 * `@typescript-eslint/prefer-nullish-coalescing` rejects both `||` and the `x ? x : y`
 * ternary — correctly, in general. This is the one exception, so it is written down once,
 * with the reason, instead of five suppressions with none.
 *
 * Generalised beyond `string` for SYM-496: the chart config carries the same idiom over
 * numbers (`barTickOption.fontSize`, `horizontalLine.label.borderWidth`) and over values that
 * arrive as `null` rather than absent, and a `0` there means "use the default" exactly as an
 * empty string does for a colour. The `!== undefined` fallbacks that also appear in the same
 * legacy files are *not* this function — those are plain `??`, and the difference is what a
 * configured `0` means.
 */
export function configuredOr<T extends string | number | boolean>(
  value: T | undefined | null,
  fallback: T,
): T {
  // Falsy is absent. For a string that is exactly the original `=== '' || === undefined`.
  if (!value) return fallback;
  return value;
}
